// One build in a queue: the assembly, how many, when it's needed, whether
// it's on time, and where it stands (T2).
import { Link } from 'react-router';

import type { BuildSummaryResponse } from '../../api/generated/BuildSummaryResponse';
import { Badge, ProgressBar, ScheduleBadge, StateBadge } from '../../design/components';
import { formatDate, formatDuration } from '../../design/format';

export function progressText(build: BuildSummaryResponse): string {
    const { progress } = build;
    switch (build.state) {
        case 'Ready':
            return 'Ready';
        case 'Built':
            return `Built ${formatDate(build.signedOffAt)}`;
        case 'OnHold':
            return `On hold: ${build.holdReason ?? 'held'}`;
        case 'InProgress': {
            const unit = progress.units > 1 ? `Unit ${String(progress.unit)} of ${String(progress.units)} · ` : '';
            return `${unit}In progress (step ${String(Math.min(progress.stepsDone + 1, progress.stepsTotal))} of ${String(progress.stepsTotal)})`;
        }
    }
}

export function waitingText(build: BuildSummaryResponse): string | null {
    if (build.waitingOn.length === 0) return null;
    return build.waitingOn
        .map((waiting) => {
            const what = waiting.count === 1 ? '1 sub-assembly' : `${String(waiting.count)} sub-assemblies`;
            return `Waiting on ${what}${waiting.workCenter ? ` from ${waiting.workCenter}` : ''}`;
        })
        .join('; ');
}

export function BuildCard({ build, to }: { build: BuildSummaryResponse; to: string }) {
    const waiting = waitingText(build);
    return (
        <Link
            to={to}
            className="sf-card sf-build-card"
            aria-label={`${build.assemblyPartNumber} ${build.assemblyName ?? ''}: ${progressText(build)}`}
        >
            <div className="sf-row" style={{ justifyContent: 'space-between' }}>
                <strong className="sf-mono">{build.assemblyPartNumber}</strong>
                <StateBadge state={build.state} />
            </div>
            <div>{build.assemblyName ?? 'Unnamed assembly'}</div>
            <div className="sf-row">
                <Badge>× {build.buildQuantity}</Badge>
                <span className="sf-muted">Need by {formatDate(build.needBy)}</span>
                {build.state === 'Built' ? null : (
                    <ScheduleBadge schedule={build.schedule} reason={build.scheduleReason} />
                )}
                {build.queuePosition === null ? null : <Badge tone="neutral">Lead’s #{build.queuePosition}</Badge>}
            </div>
            <div>{progressText(build)}</div>
            {build.state === 'InProgress' ? (
                <ProgressBar done={build.progress.stepsDone} total={build.progress.stepsTotal} label="Steps done" />
            ) : null}
            {build.blockedSince ? (
                <div className="sf-muted">Blocked for {formatDuration(build.blockedSince)}</div>
            ) : null}
            {waiting ? <div className="sf-note sf-note--info">{waiting}</div> : null}
            {build.latestHandover ? (
                <div className="sf-muted">
                    Handover from {build.latestHandover.left.displayName}: “{build.latestHandover.note}”
                </div>
            ) : null}
        </Link>
    );
}
